import { Component, OnInit } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../core/auth/auth.service';
import { ComponentBase } from '../../component.base';
import { KeyValuePairModel } from '../../models/common.models';
import { ICmsUserDetailsModel } from '../../models/user-management.models';
import { SiteManagementService } from '../../services/site-management.service';
import { UserManagementService } from '../../services/user-management.service';
import { GlobalFilterService } from 'app/shared/services/global-filter-state.service';
import { GlobalFilterStateService } from 'app/shared/services/global-filter-state.service.old';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  FilterStateService,
} from "app/shared/services/filter-state.service";
@Component({
  selector: 'app-global-filter',
  templateUrl: './global-filter.component.html',
  styleUrl: './global-filter.component.scss',
  standalone: true,
  imports: [MatSelectModule, MatDividerModule, MatMenuModule],
})
export class GlobalFilterComponent extends ComponentBase implements OnInit {
  private readonly defaultSiteId = 0;
  private readonly filterName = 'sites';
  private readonly filterLabel = 'Site';
  private readonly filterType = 'custom-select';
  sites: KeyValuePairModel<number>[] = [];
  selectedSites: number[] = [];
  showAllSites = true;
  userDetails: ICmsUserDetailsModel;

  constructor(
    private filterStateService: FilterStateService,
    private globalFilterService: GlobalFilterService,
    private globalFilterService_OLD: GlobalFilterStateService,
    private siteService: SiteManagementService,
    private userManagementService: UserManagementService,
    private authService: AuthService,
    private router: Router,
    private toastr: ToastrService
  ) {
    super();
  }

  ngOnInit() {
    // set state
    this.selectedSites = this.globalFilterService.getCachedSites();
    this.globalFilterService.selectedSites =
      this.globalFilterService.getCachedSites();

    // load data
    this.loadData();
    this.filterStateService.updateOccurred
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.resetSites());

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.setExistingSiteFilters());
  }

  resetSites() {
    this.selectedSites = this.globalFilterService.getCachedSites();
  }

  loadData(): void {
    this.authService.userProfile$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((userProfile) => {
        const userDetails$ = this.userManagementService.getCmsUserV1(
          userProfile['sub']
        ).pipe(
          catchError(() => {
            this.toastr.warning(
              'Unable to load user details. Showing all sites.',
              'Warning'
            );
            // Return a default user object that allows all sites
            return of({
              allSites: true,
              sites: [],
              primarySite: null
            } as ICmsUserDetailsModel);
          })
        );
        const sites$ = this.siteService.getSitesLookupV1().pipe(
          catchError(() => {
            this.toastr.error(
              'Unable to load sites. Please refresh the page.',
              'Error'
            );
            return of([]);
          })
        );

        forkJoin({
          userDetails: userDetails$,
          sites: sites$
        })
          .pipe(takeUntil(this.ngUnsubscribe$))
          .subscribe({
            next: ({ userDetails, sites }) => {
              this.userDetails = userDetails;

              if (!this.userDetails.allSites) {
                const filteredSites = sites.filter((x) =>
                  this.userDetails.sites.map((x) => x.id).includes(x.id)
                );
                this.sites = filteredSites;
              } else {
                this.sites = sites;
              }

              // update the global filter service with the sites
              this.globalFilterService.sites = this.sites;

              this.setExistingSiteFilters();
            }
          });
      });
  }

  onSiteSelected = (sites: number[]) => {
    let sitesToUpdate = [];

    // if the current selected site is the default site and
    // the user selects another site, remove the default site
    if (
      this.selectedSites.length === 1 &&
      this.selectedSites.includes(this.defaultSiteId) &&
      sites.some((s) => s !== this.defaultSiteId)
    ) {
      sitesToUpdate = sites.filter((s) => s !== this.defaultSiteId);
    }

    // if the user selects the default site and the current selected sites
    // do not contain the default site, remove all sites except the default site
    else if (
      (!this.selectedSites.includes(this.defaultSiteId) &&
        sites.includes(this.defaultSiteId)) ||
      !sites?.length
    ) {
      sitesToUpdate = [this.defaultSiteId];
    } else {
      // otherwise, just update the selected sites
      sitesToUpdate = sites;
    }

    localStorage.setItem('sites', sitesToUpdate.join(','));

    this.selectedSites = sitesToUpdate;

    this.globalFilterService.selectedSites = sitesToUpdate;

    this.globalFilterService_OLD.setValue(
      this.filterName,
      this.sites.filter((x) => sitesToUpdate.includes(x.id)),
      this.filterType,
      this.filterLabel,
      true
    );

  };

  setExistingSiteFilters(): void {
    const existingSitesFilter =
      localStorage
        .getItem(this.filterName)
        ?.split(',')
        .map((x) => +x)
        .filter((x) => x > 0) ?? [];

    if (existingSitesFilter.length === 0) {
      if (this.userDetails.allSites) {
        this.globalFilterService_OLD.resetKey(this.filterName);
        return;
      }

      if (this.userDetails?.primarySite) {
        this.selectedSites = [this.userDetails.primarySite.id];
        this.globalFilterService.selectedSites = [
          this.userDetails.primarySite.id,
        ];
        this.globalFilterService_OLD.setValue(
          this.filterName,
          [this.userDetails.primarySite],
          this.filterType,
          this.filterLabel,
          true
        );
        return;
      }

      if (!this.userDetails.allSites && !this.userDetails.primarySite) {
        this.globalFilterService_OLD.setValue(
          this.filterName,
          this.sites,
          this.filterType,
          this.filterLabel,
          true
        );
      }

      return;
    }

    const siteFilters = this.sites.filter((x) =>
      existingSitesFilter.includes(x.id)
    );
    this.globalFilterService_OLD.setValue(
      this.filterName,
      siteFilters,
      this.filterType,
      this.filterLabel,
      true
    );
  }
}
